import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "../utils";
import request from "@/lib/api/handler";

export interface Executive {
  id: string;
  name?: string;
  profilePicture?: string;
  role?: string;
  engagementType?: string;
  experience?: string;
  startDate?: string;
  linkedIn?: string;
  cultureLink?: string;
  biography?: string;
}

export interface Company {
  id?: string;
  name?: string;
  location?: string;
  logoLink?: string;
  foundedYear?: string;
  companySize?: string;
  fundsRaised?: string;
  lastFundraise?: string;
  cultureStatements?: string;
  linkedIn?: string;
  websiteLink?: string;
  aboutUs?: string;
  missionStatement?: string;
  profileComplete?: boolean;
  onboardingComplete?: boolean;
  researchInProgress?: boolean;
  executiveTeam: Executive[];
}

export const companyUserAccountTypeList = ["Admin", "Collaborator"] as const;
export type CompanyUserAccountType =
  (typeof companyUserAccountTypeList)[number];

export interface TeamMember {
  id: string;
  userId: string;
  attioId?: string;
  name?: string;
  emails: string[];
  email?: string;
  profilePictureLink?: string;
  accountType: CompanyUserAccountType;
  onboarded?: boolean;
}

export const useCompanyTeamQuery = (companyId?: string) => {
  const { getAccessTokenSilently } = useAuth0();

  const { isLoading, data, error } = useQuery({
    queryKey: QueryKeys.companyTeam(companyId ?? ""),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!companyId,
    async queryFn() {
      const token = await getAccessTokenSilently();
      const resp: TeamMember[] = await request({
        method: "GET",
        path: `/api/company/${companyId}/team`,
        token,
      });

      return resp;
    },
  });

  return {
    companyTeamLoading: isLoading,
    companyTeam: data ?? null,
    companyTeamError: error ?? null,
  };
};
